import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Clock,
  Check,
  Accessibility,
  Handshake,
  Route,
  Sparkles,
  Briefcase,
  TrendingDown,
  Brain,
  FileText,
} from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import usePageMeta from "@/hooks/usePageMeta";
import { artikelen } from "@/data/kennisbank";
import KennisbankSubscribe from "@/components/KennisbankSubscribe";

const iconMap = {
  accessibility: Accessibility,
  handshake: Handshake,
  route: Route,
  spark: Sparkles,
  briefcase: Briefcase,
  "trending-down": TrendingDown,
  brain: Brain,
  "file-text": FileText,
};

export default function Kennisbank() {
  usePageMeta({
    title: "Kennisbank, LevelLeven",
    description:
      "Editoriale artikelen over gedragsverandering, arbeidsmarkt en participatiebeleid, gebaseerd op peer-reviewed bronnen.",
  });
  return (
    <div>
      <section className="py-16 sm:py-24 border-b border-[#1F5132]/10">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl">
            <SectionLabel>Kennisbank</SectionLabel>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#1F5132] font-light">
              Wat de wetenschap zegt over{" "}
              <span className="italic text-[#E06B3F]">stilstand</span>.
            </h1>
            <p className="mt-8 text-lg text-[#1F5132] leading-relaxed font-light">
              Editoriale artikelen voor wethouders, beleidsmakers en professionals.
              Geen meningen, bevindingen uit peer-reviewed onderzoek, vertaald
              naar de praktijk van gemeentelijke re-integratie.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {artikelen.map((a, i) => {
              const Icon = iconMap[a.icon] ?? FileText;
              return (
                <motion.article
                  key={a.slug}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.7, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  className="group"
                >
                  <Link
                    to={`/kennisbank/${a.slug}`}
                    data-testid={`kb-artikel-${a.slug}`}
                    className="flex flex-col h-full bg-[#FAF7F1] border border-[#1F5132]/10 rounded-2xl p-7 sm:p-8 hover:border-[#E06B3F] hover:bg-[#FAF7F1] transition-colors"
                  >
                    <div className="flex items-start justify-between mb-6">
                      <div className="w-12 h-12 rounded-full bg-[#E06B3F]/10 text-[#E06B3F] flex items-center justify-center">
                        <Icon size={20} />
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-[#E06B3F]">
                          {a.thema}
                        </p>
                        <p className="mt-1 text-[10px] uppercase tracking-[0.25em] text-[#3E4A42]">
                          {a.datum}
                        </p>
                      </div>
                    </div>

                    <h2 className="font-heading text-2xl sm:text-[26px] text-[#1F5132] leading-snug font-light group-hover:text-[#E06B3F] transition-colors">
                      {a.titel}
                    </h2>
                    <p className="mt-3 text-sm text-[#1F5132] leading-relaxed">
                      {a.subtitel}
                    </p>

                    {a.takeaways?.length > 0 && (
                      <ul className="mt-6 space-y-3 border-t border-[#1F5132]/10 pt-6">
                        {a.takeaways.slice(0, 3).map((t, ti) => (
                          <li
                            key={ti}
                            className="flex items-start gap-3 text-sm text-[#1F5132] leading-snug"
                          >
                            <Check size={14} className="text-[#E06B3F] mt-0.5 shrink-0" />
                            <span className="text-[#1F5132]">{t}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="mt-auto pt-8 flex items-center justify-between text-xs text-[#3E4A42]">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock size={12} /> {a.leesduur}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-[#1F5132] group-hover:text-[#E06B3F] transition-colors">
                        Lees artikel <ArrowUpRight size={14} />
                      </span>
                    </div>
                  </Link>
                </motion.article>
              );
            })}
          </div>

          <div className="mt-16 max-w-2xl">
            <p className="text-sm text-[#3E4A42] leading-relaxed">
              Alle bronnen worden vermeld volgens APA7. Bevindingen worden
              gebracht zonder overinterpretatie. Waar bewijs beperkt is,
              benoemen we dat expliciet.
            </p>
          </div>

          {/* SUBSCRIBE */}
          <div className="mt-16">
            <KennisbankSubscribe />
          </div>
        </div>
      </section>
    </div>
  );
}
